import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Puck, Config } from "@measured/puck";
import "@measured/puck/dist/index.css";
import { organizationConfig, eventConfig } from "../../../../lib/puck/config";
import { IOrganizationProps } from "../../../../types/Organization";
import { IEventProps, IEventTicketDetailsProps } from "../../../../types/Event";
import { ISmallEventProps } from "../../../../types/Event";
import { usePerformRequest } from "../../../../lib/usePerformRequest";
import { Endpoints } from "../../../../lib/endpoints";
import { Loader } from "../../../../components/ui/loader";
import { Button } from "@mantine/core";
import { ArrowLeft, Save } from "lucide-react";
import { useToastNotification } from "../../../../hooks/useToast";
import { PerformRequest } from "../../../../lib/usePerformRequest";

const PageBuilder: React.FC = () => {
  const { id, pageType: urlPageType } = useParams<{
    id: string;
    pageType: "organization" | "event";
  }>();
  const navigate = useNavigate();
  const toast = useToastNotification();

  const pageType = (urlPageType as "organization" | "event") || "organization";
  const entityId = id ? parseInt(id, 10) : null;

  const [data, setData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch organization or event data
  const organizationEndpoint = useMemo(() => {
    if (pageType === "organization" && entityId) {
      return Endpoints.GetSingleOrganization(entityId);
    }
    return null;
  }, [pageType, entityId]);

  const eventEndpoint = useMemo(() => {
    if (pageType === "event" && entityId) {
      return Endpoints.GetSingleEvent(entityId);
    }
    return null;
  }, [pageType, entityId]);

  const { data: organizationData, isLoading: isLoadingOrg } =
    usePerformRequest<IOrganizationProps>({
      method: organizationEndpoint?.method as any,
      route: organizationEndpoint?.route || "",
      doNotUseAuthorization: false,
    });

  const { data: eventData, isLoading: isLoadingEvent } = usePerformRequest<{
    event: IEventProps;
    ticket_sales?: IEventTicketDetailsProps[];
  }>({
    method: eventEndpoint?.method as any,
    route: eventEndpoint?.route || "",
    doNotUseAuthorization: false,
  });

  const organization = useMemo(() => {
    if (!organizationData) return null;
    const data = (organizationData as any)?.data || organizationData;
    return data as IOrganizationProps;
  }, [organizationData]);

  const event = useMemo(() => {
    if (!eventData) return null;
    const data = (eventData as any)?.event || (eventData as any)?.data?.event;
    return data as IEventProps;
  }, [eventData]);

  const tickets = useMemo(() => {
    if (!eventData) return [];
    return (eventData as any)?.ticket_sales || [];
  }, [eventData]);

  // Fetch events for organization
  const { data: eventsData } = usePerformRequest<ISmallEventProps[]>({
    method: organization ? Endpoints.GetOrganizationEvents(organization.id).method as any : "GET",
    route: organization ? Endpoints.GetOrganizationEvents(organization.id).route : "",
    doNotUseAuthorization: false,
  });

  const events = useMemo(() => {
    if (!eventsData) return [];
    const data = (eventsData as any)?.data || eventsData;
    return Array.isArray(data) ? data : [];
  }, [eventsData]);

  // Load saved page data
  useEffect(() => {
    if (entityId) {
      const saved = localStorage.getItem(`puck-${pageType}-${entityId}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setData(parsed);
        } catch (e) {
          console.error("Failed to parse saved data", e);
        }
      }
    }
  }, [entityId, pageType]);

  const config: Config = pageType === "organization" ? organizationConfig : eventConfig;

  // Prepare root props for Puck components
  const rootProps = useMemo(() => {
    if (pageType === "organization") {
      return {
        organization,
        events,
      };
    } else {
      return {
        event,
        tickets,
      };
    }
  }, [pageType, organization, events, event, tickets]);

  // Merge rootProps into data
  const puckData = useMemo(() => {
    if (!data) {
      return {
        content: [],
        root: rootProps,
      };
    }
    return {
      ...data,
      root: {
        ...data.root,
        ...rootProps,
      },
    };
  }, [data, rootProps]);

  const handleSave = async () => {
    if (!data || !entityId) return;

    setIsSaving(true);
    try {
      // Save to localStorage for now
      localStorage.setItem(`puck-${pageType}-${entityId}`, JSON.stringify(data));
      toast.success("Page saved successfully!");
      // TODO: Save to API when backend is ready
      // const endpoint = pageType === "organization"
      //   ? Endpoints.SaveOrganizationPageSchema(entityId)
      //   : Endpoints.SaveEventPageSchema(entityId);
      // await PerformRequest({
      //   method: endpoint.method as any,
      //   route: endpoint.route,
      //   data: { schema: JSON.stringify(data) },
      // });
    } catch (error) {
      toast.error("Failed to save page");
      console.error("Save error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingOrg || isLoadingEvent) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Toolbar */}
      <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Button
            variant="subtle"
            leftSection={<ArrowLeft size={18} />}
            onClick={() => {
              if (pageType === "organization") {
                navigate(`/d/organizations/${entityId}`);
              } else {
                navigate(`/d/events/${entityId}`);
              }
            }}
          >
            Back
          </Button>
        </div>
        <Button
          leftSection={<Save size={18} />}
          onClick={handleSave}
          loading={isSaving}
          style={{ backgroundColor: "#17265a" }}
        >
          Save Page
        </Button>
      </div>

      {/* Puck Editor */}
      <div className="flex-1 overflow-hidden">
        <Puck
          config={config}
          data={puckData}
          onPublish={setData}
        />
      </div>
    </div>
  );
};

export default PageBuilder;
